// Small shared presentational bits for the Cases table: a colored status pill
// and a stacked initials-avatar group for the case team. Both are pure.

import { getStatusBucket } from "../../../utils/caseStatus";

const PILL = {
  pending: "bg-amber-50 text-amber-700 ring-amber-200",
  disposed: "bg-violet-50 text-violet-700 ring-violet-200",
};

// Colored status pill. Falls back to a neutral slate pill for unknown buckets.
export function StatusPill({ status }) {
  const bucket = getStatusBucket(status);
  const cls = PILL[bucket] || "bg-slate-100 text-slate-600 ring-slate-200";
  const text = status
    ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
    : "—";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${cls}`}
    >
      {text}
    </span>
  );
}

// Deterministic soft color per name so the same person keeps the same avatar
// color across rows.
const AVATAR_COLORS = [
  "bg-blue-100 text-blue-700",
  "bg-emerald-100 text-emerald-700",
  "bg-amber-100 text-amber-700",
  "bg-violet-100 text-violet-700",
  "bg-rose-100 text-rose-700",
  "bg-sky-100 text-sky-700",
];

function colorFor(seed) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

function initialOf(name) {
  const t = (name || "").trim();
  return t ? t.charAt(0).toUpperCase() : "?";
}

// Stacked avatar group. `members` = [{ uid, name }]. Shows up to `max`
// initials-circles then a "+N" overflow chip.
export function TeamAvatars({ members = [], max = 3 }) {
  if (!members.length) {
    return <span className="text-sm text-slate-400">—</span>;
  }
  const shown = members.slice(0, max);
  const extra = members.length - shown.length;
  return (
    <div className="flex items-center -space-x-2">
      {shown.map((m, i) => (
        <span
          key={m.uid || i}
          title={m.name || m.uid}
          className={`inline-flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold ring-2 ring-white ${colorFor(
            m.uid || m.name || String(i)
          )}`}
        >
          {initialOf(m.name || m.uid)}
        </span>
      ))}
      {extra > 0 && (
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600 ring-2 ring-white">
          +{extra}
        </span>
      )}
    </div>
  );
}
